import { useEffect, useState, useMemo, useCallback, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    listFiles,
    deleteFileById,
    fetchFileUsage,
    trackUntrackedFile,
    type FileRecord,
    type FileUsageReport,
    type TrackedFileUsage,
    type UntrackedFileOnDisk,
} from "../api/file";
import { resolveLabwickUploadUrl } from "../utils/pictureUtils";
import { fetchComponentsFromAPI } from "../api/componentsApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuCheckboxItem,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { RefreshCw, Trash2, FileText, ExternalLink, BarChart3, Lock, Users, Globe, Pencil, ArrowUp, ArrowDown, ChevronsUpDown, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { buildCollectionPath } from "../utils/collectionPaths";
import { useAuth } from "../auth/AuthContext";
import { authAPI } from "../api/authApi";
import { FileSharingEditorDialog } from "./FileSharingEditorDialog";

function formatSize(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes < 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value?: string): string {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function slugForCollection(
    tableName: string,
    components: Array<{ slug?: string; mongoCollection?: string; collection?: string }>
): string | null {
    const t = (tableName || "").trim();
    if (!t) return null;
    const c = components.find(
        (m) =>
            (m.mongoCollection && m.mongoCollection === t) ||
            (m.collection && m.collection === t)
    );
    return c?.slug ?? null;
}

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|svg|bmp|ico)$/i;

function isImagePath(name: string): boolean {
    return IMAGE_EXT_RE.test(String(name || "").split("/").pop() || "");
}

function isImageFile(name: string, mimeType?: string): boolean {
    if (mimeType && /^image\//i.test(mimeType)) return true;
    return isImagePath(name);
}

const FILES_PATH = "/files";

type UsageFilterTab = "all" | "in-use" | "orphaned" | "untracked";

/** Sharing-scope filter keys for the library table (OR-combined within the column). */
type SharingFilterKey =
    | "owned"
    | "shared-with-me"
    | "private"
    | "shared"
    | "public"
    | "not-public";

const SHARING_FILTER_OPTIONS: Array<{ value: SharingFilterKey; label: string }> = [
    { value: "owned", label: "Your files" },
    { value: "shared-with-me", label: "Shared with you" },
    { value: "private", label: "Private in Labwick" },
    { value: "shared", label: "Shared with a group" },
    { value: "public", label: "Public link" },
    { value: "not-public", label: "Not public" },
];

/** Sortable columns in the library table. */
type LibrarySortKey = "name" | "size" | "type" | "user" | "created" | "updated";
type SortDir = "asc" | "desc";

function SummaryStat({ label, value, tone }: { label: string; value: number; tone?: "ok" | "warn" }) {
    const color = tone === "ok" ? "text-green-700 dark:text-green-400" : tone === "warn" ? "text-amber-600" : "";
    return (
        <div className="rounded-md border p-3">
            <div className={`text-2xl font-semibold ${color}`}>{value}</div>
            <div className="text-xs text-muted-foreground">{label}</div>
        </div>
    );
}

function UsageThumb({ name, type }: { name: string; type?: string }) {
    const url = resolveLabwickUploadUrl(name);
    if (!url || !isImageFile(name, type)) {
        return (
            <a
                href={url || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-12 w-12 items-center justify-center rounded bg-muted text-muted-foreground"
                title={name}
            >
                <FileText className="h-5 w-5" />
            </a>
        );
    }
    return (
        <a href={url} target="_blank" rel="noopener noreferrer" title={name}>
            <img src={url} alt={name} loading="lazy" className="h-12 w-12 rounded object-cover bg-muted" />
        </a>
    );
}

/** A column-header dropdown of checkboxes; selections OR-combine within the column. */
function ColumnFilter({
    options,
    selected,
    onChange,
}: {
    options: Array<{ value: string; label: string }>;
    selected: string[];
    onChange: (next: string[]) => void;
}) {
    const active = selected.length > 0;
    const toggle = (value: string, checked: boolean) =>
        onChange(checked ? [...selected, value] : selected.filter((v) => v !== value));
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    title="Filter"
                    aria-label="Filter column"
                    className={`inline-flex items-center rounded p-0.5 hover:text-foreground ${active ? "text-primary" : "text-muted-foreground/50"}`}
                >
                    <Filter className={`h-3.5 w-3.5 ${active ? "fill-current" : ""}`} />
                    {active ? <span className="ml-0.5 text-[10px] font-semibold">{selected.length}</span> : null}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-72 w-56 overflow-auto">
                {active ? (
                    <>
                        <DropdownMenuItem onClick={() => onChange([])} className="text-xs">
                            Clear filter
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                    </>
                ) : null}
                {options.length === 0 ? (
                    <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                        No values
                    </DropdownMenuLabel>
                ) : (
                    options.map((o) => (
                        <DropdownMenuCheckboxItem
                            key={o.value}
                            checked={selected.includes(o.value)}
                            onCheckedChange={(c) => toggle(o.value, Boolean(c))}
                            onSelect={(e) => e.preventDefault()}
                        >
                            {o.label}
                        </DropdownMenuCheckboxItem>
                    ))
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

function pageWindow<T>(rows: T[], page: number, pageSize: number) {
    const size = Math.max(1, pageSize);
    const total = rows.length;
    const pageCount = Math.max(1, Math.ceil(total / size) || 1);
    const safePage = Math.min(Math.max(1, page), pageCount);
    const start = (safePage - 1) * size;
    return {
        rows: rows.slice(start, start + size),
        page: safePage,
        pageSize: size,
        total,
        pageCount,
    };
}

function TablePager({
    page,
    pageSize,
    total,
    onPageChange,
    onPageSizeChange,
}: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
}) {
    const pageCount = Math.max(1, Math.ceil(total / Math.max(1, pageSize)) || 1);
    const range =
        total === 0
            ? "0 rows"
            : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, total)} of ${total}`;
    return (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
                <span>Rows per page</span>
                <Select
                    value={String(pageSize)}
                    onValueChange={(value) => {
                        const next = parseInt(value, 10);
                        if (!Number.isNaN(next)) onPageSizeChange(next);
                    }}
                >
                    <SelectTrigger className="h-8 w-[72px]" aria-label="Rows per page">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {PAGE_SIZE_OPTIONS.map((n) => (
                            <SelectItem key={n} value={String(n)}>
                                {n}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="flex items-center gap-3">
                <span>{range}</span>
                <div className="flex items-center gap-1">
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        disabled={page <= 1}
                        onClick={() => onPageChange(page - 1)}
                        aria-label="Previous page"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        disabled={total === 0 || page >= pageCount}
                        onClick={() => onPageChange(page + 1)}
                        aria-label="Next page"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

function SortableHead({
    label,
    column,
    sortKey,
    sortDir,
    onSort,
    className,
    filter,
}: {
    label: string;
    column: LibrarySortKey;
    sortKey: LibrarySortKey;
    sortDir: SortDir;
    onSort: (key: LibrarySortKey) => void;
    className?: string;
    filter?: ReactNode;
}) {
    const active = sortKey === column;
    const Icon = !active ? ChevronsUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
    return (
        <TableHead className={className}>
            <span className="inline-flex items-center gap-1">
                <button
                    type="button"
                    onClick={() => onSort(column)}
                    className="inline-flex items-center gap-1 hover:text-foreground"
                    aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                >
                    {label}
                    <Icon className={`h-3.5 w-3.5 ${active ? "" : "text-muted-foreground/60"}`} />
                </button>
                {filter}
            </span>
        </TableHead>
    );
}

const FilesPage = () => {
    const { user: authUser } = useAuth();
    const [files, setFiles] = useState<FileRecord[]>([]);
    const [usageReport, setUsageReport] = useState<FileUsageReport | null>(null);
    const [components, setComponents] = useState<any[]>([]);
    const [groups, setGroups] = useState<Array<{ uid: string; title: string }>>([]);
    const [peers, setPeers] = useState<Array<{ uid: string; name: string }>>([]);
    const [loading, setLoading] = useState(true);
    const [usageLoading, setUsageLoading] = useState(false);
    const [usageError, setUsageError] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [trackingName, setTrackingName] = useState<string | null>(null);
    const [usageTab, setUsageTab] = useState<UsageFilterTab>("all");
    const [usageSearch, setUsageSearch] = useState("");
    const [activeTab, setActiveTab] = useState("library");
    const [editingFile, setEditingFile] = useState<FileRecord | null>(null);
    const [librarySearch, setLibrarySearch] = useState("");
    const [typeFilter, setTypeFilter] = useState<string[]>([]);
    const [uploaderFilter, setUploaderFilter] = useState<string[]>([]);
    const [sharingFilter, setSharingFilter] = useState<string[]>([]);
    const [sortKey, setSortKey] = useState<LibrarySortKey>("name");
    const [sortDir, setSortDir] = useState<SortDir>("asc");
    const [libraryPage, setLibraryPage] = useState(1);
    const [libraryPageSize, setLibraryPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [usagePage, setUsagePage] = useState(1);
    const [usagePageSize, setUsagePageSize] = useState(DEFAULT_PAGE_SIZE);
    const location = useLocation();

    const groupTitleByUid = useMemo(() => {
        const map = new Map<string, string>();
        for (const g of groups) map.set(g.uid, g.title);
        return map;
    }, [groups]);

    const userNameByUid = useMemo(() => {
        const map = new Map<string, string>();
        for (const p of peers) map.set(p.uid, p.name);
        // The current user may not be in their own peers list; include them.
        if (authUser?.uid) {
            const self = [authUser.firstName, authUser.lastName].filter(Boolean).join(" ").trim();
            if (self || authUser.email) map.set(authUser.uid, self || authUser.email || authUser.uid);
        }
        return map;
    }, [peers, authUser?.uid, authUser?.firstName, authUser?.lastName, authUser?.email]);

    const uploaderLabel = useCallback(
        (uid: string | null | undefined) => (uid ? (userNameByUid.get(uid) || uid) : "—"),
        [userNameByUid]
    );

    const canManageSharing = useCallback(
        (file: FileRecord) => Boolean(authUser?.admin || (file.user && file.user === authUser?.uid)),
        [authUser]
    );

    const canDeleteFile = useCallback(
        (file: FileRecord) => {
            if (authUser?.admin) return true;
            if (!file.user || file.user !== authUser?.uid) return false;
            return true;
        },
        [authUser]
    );

    const loadFiles = useCallback(async () => {
        setLoading(true);
        const res = await listFiles();
        if (res?.success && Array.isArray(res.files)) {
            setFiles(res.files);
        } else {
            setFiles([]);
        }
        setLoading(false);
    }, []);

    const loadUsage = useCallback(async () => {
        setUsageLoading(true);
        setUsageError(null);
        const res = await fetchFileUsage();
        if (res?.success) {
            setUsageReport(res);
        } else {
            setUsageReport(null);
            setUsageError(res?.message || "Failed to load usage report");
        }
        setUsageLoading(false);
    }, []);

    const loadComponents = async () => {
        try {
            const list = await fetchComponentsFromAPI();
            setComponents(Array.isArray(list) ? list : []);
        } catch {
            setComponents([]);
        }
    };

    const loadGroups = async () => {
        try {
            const list = await authAPI.getMyGroups();
            setGroups(Array.isArray(list) ? list : []);
        } catch {
            setGroups([]);
        }
    };

    const loadPeers = async () => {
        try {
            const list = await authAPI.getPeers();
            setPeers(Array.isArray(list) ? list : []);
        } catch {
            setPeers([]);
        }
    };

    useEffect(() => {
        loadComponents();
        loadGroups();
        loadPeers();
    }, []);

    useEffect(() => {
        if (location.pathname === FILES_PATH) {
            loadFiles();
        }
    }, [location.pathname, loadFiles]);

    useEffect(() => {
        if (location.pathname === FILES_PATH && activeTab === "usage") {
            loadUsage();
        }
    }, [location.pathname, activeTab, loadUsage]);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState !== "visible" || location.pathname !== FILES_PATH) return;
            if (activeTab === "library") loadFiles();
            if (activeTab === "usage") loadUsage();
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => document.removeEventListener("visibilitychange", onVisibilityChange);
    }, [location.pathname, activeTab, loadFiles, loadUsage]);

    const handleDelete = async (fileId: string) => {
        if (!confirm("Delete this file? This will remove the file and clear its links from all items.")) return;
        setDeletingId(fileId);
        const res = await deleteFileById(fileId);
        setDeletingId(null);
        if (res?.success) {
            setFiles((prev) => prev.filter((f) => f.uid !== fileId));
            if (activeTab === "usage") void loadUsage();
        } else {
            alert(res?.message || "Failed to delete file");
        }
    };

    const handleTrack = async (name: string) => {
        setTrackingName(name);
        const res = await trackUntrackedFile(name);
        setTrackingName(null);
        if (res?.success) {
            // The file now has a record: refresh usage (moves it out of untracked) and the library.
            void loadUsage();
            void loadFiles();
        } else {
            alert(res?.message || "Failed to track file");
        }
    };

    const toggleSort = useCallback((key: LibrarySortKey) => {
        setSortKey((prevKey) => {
            if (prevKey === key) {
                setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                return prevKey;
            }
            setSortDir("asc");
            return key;
        });
    }, []);

    const matchesSharing = useCallback(
        (file: FileRecord, key: string) => {
            switch (key) {
                case "owned":
                    return Boolean(file.user && file.user === authUser?.uid);
                case "shared-with-me":
                    return Boolean(file.user && file.user !== authUser?.uid);
                case "private":
                    return file.visibility !== "shared";
                case "shared":
                    return file.visibility === "shared";
                case "public":
                    return file.public !== false;
                case "not-public":
                    return file.public === false;
                default:
                    return true;
            }
        },
        [authUser?.uid]
    );

    // Distinct values for the per-column filter menus, derived from the loaded files.
    const typeOptions = useMemo(() => {
        const set = new Set<string>();
        for (const f of files) set.add(f.type || "");
        return [...set]
            .sort((a, b) => a.localeCompare(b))
            .map((t) => ({ value: t, label: t || "(no type)" }));
    }, [files]);

    const uploaderOptions = useMemo(() => {
        const set = new Set<string>();
        for (const f of files) set.add(f.user || "");
        return [...set]
            .map((uid) => ({ value: uid, label: uid ? (userNameByUid.get(uid) || uid) : "(no uploader)" }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [files, userNameByUid]);

    useEffect(() => {
        setLibraryPage(1);
    }, [librarySearch, typeFilter, uploaderFilter, sharingFilter, sortKey, sortDir]);

    useEffect(() => {
        setUsagePage(1);
    }, [usageSearch, usageTab]);

    const filtersActive = typeFilter.length > 0 || uploaderFilter.length > 0 || sharingFilter.length > 0;
    const clearAllFilters = useCallback(() => {
        setTypeFilter([]);
        setUploaderFilter([]);
        setSharingFilter([]);
    }, []);

    const visibleFiles = useMemo(() => {
        const groupTitlesFor = (file: FileRecord) => {
            const uids = Array.isArray(file.groups) && file.groups.length > 0
                ? file.groups
                : (file.group ? [file.group] : []);
            return uids.map((uid) => groupTitleByUid.get(uid) || uid);
        };

        // Per-column filters: OR within a column, AND across columns.
        let rows = files.filter((file) => {
            if (typeFilter.length > 0 && !typeFilter.includes(file.type || "")) return false;
            if (uploaderFilter.length > 0 && !uploaderFilter.includes(file.user || "")) return false;
            if (sharingFilter.length > 0 && !sharingFilter.some((k) => matchesSharing(file, k))) return false;
            return true;
        });

        // Free-text search across filename, type, uploader (uid + name), group titles and links.
        const q = librarySearch.trim().toLowerCase();
        if (q) {
            rows = rows.filter((file) => {
                const haystack = [
                    file.original,
                    file.name,
                    file.type,
                    file.user,
                    file.user ? userNameByUid.get(file.user) : "",
                    ...groupTitlesFor(file),
                    ...(file.relations || []).flatMap((r) => [r.table, r.field, r.uid]),
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return haystack.includes(q);
            });
        }

        // Sort.
        const dir = sortDir === "asc" ? 1 : -1;
        const sorted = [...rows].sort((a, b) => {
            let cmp = 0;
            switch (sortKey) {
                case "size":
                    cmp = (a.size || 0) - (b.size || 0);
                    break;
                case "created":
                    cmp = String(a.createdAt || "").localeCompare(String(b.createdAt || ""));
                    break;
                case "updated":
                    cmp = String(a._updated || "").localeCompare(String(b._updated || ""));
                    break;
                case "type":
                    cmp = (a.type || "").localeCompare(b.type || "");
                    break;
                case "user":
                    cmp = uploaderLabel(a.user).localeCompare(uploaderLabel(b.user));
                    break;
                case "name":
                default:
                    cmp = (a.original || a.name || "").localeCompare(b.original || b.name || "");
                    break;
            }
            return cmp * dir;
        });
        return sorted;
    }, [files, typeFilter, uploaderFilter, sharingFilter, librarySearch, sortKey, sortDir, matchesSharing, groupTitleByUid, userNameByUid, uploaderLabel]);

    const libraryPageView = useMemo(
        () => pageWindow(visibleFiles, libraryPage, libraryPageSize),
        [visibleFiles, libraryPage, libraryPageSize]
    );

    const relationLinks = useMemo(() => {
        return (relations: Array<{ table: string; uid: string; field: string; linkedAt?: string }>) => {
            return relations.map((rel, idx) => {
                const slug = slugForCollection(rel.table, components);
                const label = slug
                    ? `${slug} · ${rel.field} (${rel.uid})`
                    : `${rel.table} / ${rel.field} / ${rel.uid}`;
                const linkedSuffix = rel.linkedAt ? (
                    <span className="text-muted-foreground" title={`Linked ${new Date(rel.linkedAt).toLocaleString()}`}>
                        {" "}· {formatDate(rel.linkedAt)}
                    </span>
                ) : null;
                if (slug) {
                    const comp = components.find((c: any) => c.slug === slug);
                    return (
                        <span key={`${rel.table}-${rel.uid}-${rel.field}-${idx}`} className="inline-flex items-center gap-1">
                            <Link
                                to={buildCollectionPath({ slug, organization: comp?.organization })}
                                className="text-blue-600 hover:underline text-xs flex items-center gap-0.5"
                            >
                                <ExternalLink className="h-3 w-3" />
                                {label}
                            </Link>
                            {linkedSuffix}
                            {idx < relations.length - 1 ? ", " : null}
                        </span>
                    );
                }
                return (
                    <span key={`${rel.table}-${rel.uid}-${idx}`} className="text-muted-foreground text-xs">
                        {label}
                        {linkedSuffix}
                        {idx < relations.length - 1 ? ", " : null}
                    </span>
                );
            });
        };
    }, [components]);

    const visibleTracked = useMemo(() => {
        if (!usageReport) return [] as TrackedFileUsage[];
        let rows = usageReport.trackedFiles;
        if (usageTab === "in-use") rows = rows.filter((f) => f.status === "in-use");
        if (usageTab === "orphaned") rows = rows.filter((f) => f.status === "orphaned");
        const q = usageSearch.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(
            (f) =>
                f.name.toLowerCase().includes(q) ||
                (f.original || "").toLowerCase().includes(q) ||
                f.relations.some(
                    (r) =>
                        r.table.toLowerCase().includes(q) ||
                        r.uid.toLowerCase().includes(q) ||
                        r.field.toLowerCase().includes(q)
                )
        );
    }, [usageReport, usageTab, usageSearch]);

    const usageTrackedPage = useMemo(
        () => pageWindow(visibleTracked, usagePage, usagePageSize),
        [visibleTracked, usagePage, usagePageSize]
    );

    const visibleUntracked = useMemo(() => {
        if (!usageReport || usageTab !== "untracked") return [] as UntrackedFileOnDisk[];
        const q = usageSearch.trim().toLowerCase();
        if (!q) return usageReport.untrackedFiles;
        return usageReport.untrackedFiles.filter((f) => f.name.toLowerCase().includes(q));
    }, [usageReport, usageTab, usageSearch]);

    const usageUntrackedPage = useMemo(
        () => pageWindow(visibleUntracked, usagePage, usagePageSize),
        [visibleUntracked, usagePage, usagePageSize]
    );

    const refreshAll = () => {
        loadComponents();
        loadGroups();
        loadPeers();
        if (activeTab === "library") loadFiles();
        else loadUsage();
    };

    const handleSharingSaved = (updated: FileRecord) => {
        setFiles((prev) => prev.map((f) => (f.uid === updated.uid ? { ...f, ...updated } : f)));
    };

    return (
        <div className="flex h-full min-h-0 flex-col overflow-y-auto overflow-x-hidden overscroll-y-contain">
            <div className="p-4 md:p-6 max-w-7xl mx-auto w-full">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            Files
                        </CardTitle>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={refreshAll}
                            disabled={loading || usageLoading}
                        >
                            <RefreshCw className={`h-4 w-4 mr-2 ${loading || usageLoading ? "animate-spin" : ""}`} />
                            Refresh
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                            <TabsList className="mb-4">
                                <TabsTrigger value="library">Library</TabsTrigger>
                                <TabsTrigger value="usage" className="gap-1.5">
                                    <BarChart3 className="h-4 w-4" />
                                    Usage report
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="library" className="mt-0 space-y-4">
                                {loading ? (
                                    <div className="flex items-center justify-center py-12">
                                        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
                                    </div>
                                ) : files.length === 0 ? (
                                    <p className="text-muted-foreground text-center py-8">
                                        No files yet. Upload files from a collection item, or ask a teammate to share a file with your group.
                                    </p>
                                ) : (
                                    <>
                                        <div className="flex flex-wrap items-center gap-2">
                                            {filtersActive ? (
                                                <Button variant="outline" size="sm" onClick={clearAllFilters} className="gap-1.5">
                                                    <Filter className="h-3.5 w-3.5" />
                                                    Clear filters
                                                </Button>
                                            ) : null}
                                            <Input
                                                className="ml-auto w-full sm:w-64"
                                                placeholder="Search file, type, uploader, group, link…"
                                                value={librarySearch}
                                                onChange={(e) => setLibrarySearch(e.target.value)}
                                            />
                                        </div>
                                        {visibleFiles.length === 0 ? (
                                            <p className="text-sm text-muted-foreground py-4">No files match.</p>
                                        ) : (
                                            <>
                                                <div className="overflow-x-auto">
                                                    <Table>
                                                        <TableHeader>
                                                            <TableRow>
                                                                <TableHead className="w-14" />
                                                                <SortableHead label="File" column="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="max-w-[11rem]" />
                                                                <SortableHead label="Size" column="size" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                                                <SortableHead
                                                                    label="Type"
                                                                    column="type"
                                                                    sortKey={sortKey}
                                                                    sortDir={sortDir}
                                                                    onSort={toggleSort}
                                                                    filter={<ColumnFilter options={typeOptions} selected={typeFilter} onChange={setTypeFilter} />}
                                                                />
                                                                <SortableHead
                                                                    label="Uploaded by"
                                                                    column="user"
                                                                    sortKey={sortKey}
                                                                    sortDir={sortDir}
                                                                    onSort={toggleSort}
                                                                    filter={<ColumnFilter options={uploaderOptions} selected={uploaderFilter} onChange={setUploaderFilter} />}
                                                                />
                                                                <SortableHead label="Uploaded" column="created" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                                                <TableHead>
                                                                    <span className="inline-flex items-center gap-1">
                                                                        Sharing
                                                                        <ColumnFilter options={SHARING_FILTER_OPTIONS} selected={sharingFilter} onChange={setSharingFilter} />
                                                                    </span>
                                                                </TableHead>
                                                                <TableHead>Linked to</TableHead>
                                                                <TableHead className="w-[80px]">Actions</TableHead>
                                                            </TableRow>
                                                        </TableHeader>
                                                        <TableBody>
                                                            {libraryPageView.rows.map((file) => {
                                                                const isOwned = Boolean(file.user && file.user === authUser?.uid);
                                                                const sharedGroupUids = Array.isArray(file.groups) && file.groups.length > 0
                                                                    ? file.groups
                                                                    : (file.group ? [file.group] : []);
                                                                const sharedGroupTitle = sharedGroupUids
                                                                    .map((uid) => groupTitleByUid.get(uid))
                                                                    .filter(Boolean)
                                                                    .join(", ");
                                                                return (
                                                                    <TableRow key={file.uid}>
                                                                        <TableCell>
                                                                            <UsageThumb name={file.name} type={file.type} />
                                                                        </TableCell>
                                                                        <TableCell className="max-w-[11rem] break-words whitespace-normal align-top">
                                                                            <a
                                                                                href={resolveLabwickUploadUrl(file.name)}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="text-blue-600 hover:underline font-medium break-words whitespace-normal"
                                                                            >
                                                                                {file.original || file.name || file.uid}
                                                                            </a>
                                                                        </TableCell>
                                                                        <TableCell>{formatSize(file.size)}</TableCell>
                                                                        <TableCell className="text-muted-foreground">{file.type || "—"}</TableCell>
                                                                        <TableCell className="text-muted-foreground" title={file.user || undefined}>{uploaderLabel(file.user)}</TableCell>
                                                                        <TableCell className="text-muted-foreground whitespace-nowrap" title={file.createdAt ? new Date(file.createdAt).toLocaleString() : undefined}>
                                                                            {formatDate(file.createdAt)}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <div className="flex flex-col gap-1.5">
                                                                                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                                                                                    {isOwned ? (
                                                                                        <span className="text-muted-foreground">Your file</span>
                                                                                    ) : (
                                                                                        <span className="inline-flex items-center gap-1 rounded bg-violet-50 px-1.5 py-0.5 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300">
                                                                                            <Users className="h-3 w-3" /> Shared with you
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                                                                                    {file.visibility === "shared" ? (
                                                                                        <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
                                                                                            <Users className="h-3 w-3" />
                                                                                            {sharedGroupTitle
                                                                                                ? `${sharedGroupUids.length > 1 ? "Groups" : "Group"}: ${sharedGroupTitle}`
                                                                                                : "Shared in Labwick"}
                                                                                        </span>
                                                                                    ) : (
                                                                                        <span className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                                                                                            <Lock className="h-3 w-3" /> Private in Labwick
                                                                                        </span>
                                                                                    )}
                                                                                    {file.public === false ? (
                                                                                        <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300" title="Not served publicly">
                                                                                            <Lock className="h-3 w-3" /> Not public
                                                                                        </span>
                                                                                    ) : (
                                                                                        <span className="inline-flex items-center gap-1 rounded bg-green-50 px-1.5 py-0.5 text-green-700 dark:bg-green-950/50 dark:text-green-300" title="Anyone with the link can open this file">
                                                                                            <Globe className="h-3 w-3" /> Public link
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                {canManageSharing(file) ? (
                                                                                    <Button
                                                                                        variant="ghost"
                                                                                        size="sm"
                                                                                        className="h-7 w-fit px-2 text-xs"
                                                                                        onClick={() => setEditingFile(file)}
                                                                                    >
                                                                                        <Pencil className="h-3 w-3 mr-1" />
                                                                                        Edit sharing
                                                                                    </Button>
                                                                                ) : null}
                                                                            </div>
                                                                        </TableCell>
                                                                        <TableCell className="max-w-[320px]">
                                                                            {Array.isArray(file.relations) && file.relations.length > 0 ? (
                                                                                <div className="flex flex-wrap gap-1">
                                                                                    {relationLinks(file.relations)}
                                                                                </div>
                                                                            ) : (
                                                                                <span className="text-muted-foreground">—</span>
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {canDeleteFile(file) ? (
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    className="text-destructive hover:text-destructive"
                                                                                    onClick={() => handleDelete(file.uid)}
                                                                                    disabled={deletingId === file.uid}
                                                                                    title="Delete file"
                                                                                >
                                                                                    <Trash2 className="h-4 w-4" />
                                                                                </Button>
                                                                            ) : (
                                                                                <span className="text-muted-foreground text-xs">—</span>
                                                                            )}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </div>
                                                <TablePager
                                                    page={libraryPageView.page}
                                                    pageSize={libraryPageView.pageSize}
                                                    total={libraryPageView.total}
                                                    onPageChange={setLibraryPage}
                                                    onPageSizeChange={(size) => {
                                                        setLibraryPageSize(size);
                                                        setLibraryPage(1);
                                                    }}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </TabsContent>

                            <TabsContent value="usage" className="mt-0 space-y-4">
                                {usageError && (
                                    <Alert variant="destructive">
                                        <AlertDescription>{usageError}</AlertDescription>
                                    </Alert>
                                )}

                                {usageLoading && (
                                    <div className="flex items-center justify-center py-12 text-muted-foreground">
                                        <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                                        Scanning uploads…
                                    </div>
                                )}

                                {usageReport && !usageLoading && (
                                    <>
                                        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                                            <SummaryStat label="Tracked in DB" value={usageReport.summary.tracked} />
                                            <SummaryStat label="In use" value={usageReport.summary.inUse} tone="ok" />
                                            <SummaryStat label="Orphaned" value={usageReport.summary.orphaned} tone="warn" />
                                            <SummaryStat label="On disk" value={usageReport.summary.onDisk} />
                                            <SummaryStat
                                                label="Untracked on disk"
                                                value={usageReport.summary.untrackedOnDisk}
                                                tone={usageReport.summary.untrackedOnDisk > 0 ? "warn" : undefined}
                                            />
                                        </div>

                                        <p className="text-xs text-muted-foreground">
                                            Tracked files have a row in the <code>files</code> collection. Orphaned files
                                            are tracked but no longer linked to any item. Untracked files exist under{" "}
                                            <code>uploads/</code> without a database record (e.g. image picker uploads before
                                            an item link was saved).
                                        </p>

                                        <div className="flex flex-wrap items-center gap-2">
                                            <Button
                                                variant={usageTab === "all" ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => setUsageTab("all")}
                                            >
                                                All tracked ({usageReport.summary.tracked})
                                            </Button>
                                            <Button
                                                variant={usageTab === "in-use" ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => setUsageTab("in-use")}
                                            >
                                                In use ({usageReport.summary.inUse})
                                            </Button>
                                            <Button
                                                variant={usageTab === "orphaned" ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => setUsageTab("orphaned")}
                                            >
                                                Orphaned ({usageReport.summary.orphaned})
                                            </Button>
                                            <Button
                                                variant={usageTab === "untracked" ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => setUsageTab("untracked")}
                                            >
                                                Untracked ({usageReport.summary.untrackedOnDisk})
                                            </Button>
                                            <Input
                                                className="ml-auto w-full sm:w-56"
                                                placeholder="Search filename, collection…"
                                                value={usageSearch}
                                                onChange={(e) => setUsageSearch(e.target.value)}
                                            />
                                        </div>

                                        {usageTab !== "untracked" ? (
                                            visibleTracked.length === 0 ? (
                                                <p className="text-sm text-muted-foreground py-4">No files match.</p>
                                            ) : (
                                                <>
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full text-sm">
                                                            <thead>
                                                                <tr className="border-b text-left text-xs text-muted-foreground">
                                                                    <th className="w-14 py-2 pr-2" />
                                                                    <th className="py-2 pr-2">Path</th>
                                                                    <th className="py-2 pr-2">Size</th>
                                                                    <th className="py-2 pr-2">Status</th>
                                                                    <th className="py-2 pr-2">Linked to</th>
                                                                    <th className="w-[80px] py-2" />
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {usageTrackedPage.rows.map((file) => (
                                                                    <tr key={file.uid} className="border-b align-top last:border-0">
                                                                        <td className="py-2 pr-2">
                                                                            <UsageThumb name={file.name} type={file.type} />
                                                                        </td>
                                                                        <td className="py-2 pr-2">
                                                                            <a
                                                                                href={resolveLabwickUploadUrl(file.name)}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="font-mono text-xs text-blue-600 hover:underline break-all"
                                                                            >
                                                                                {file.original || file.name}
                                                                            </a>
                                                                        </td>
                                                                        <td className="py-2 pr-2 whitespace-nowrap">
                                                                            {formatSize(file.size)}
                                                                        </td>
                                                                        <td className="py-2 pr-2">
                                                                            <Badge
                                                                                variant={
                                                                                    file.status === "in-use" ? "default" : "destructive"
                                                                                }
                                                                            >
                                                                                {file.status === "in-use" ? "In use" : "Orphaned"}
                                                                            </Badge>
                                                                        </td>
                                                                        <td className="py-2 pr-2 max-w-[280px]">
                                                                            {file.relations.length > 0 ? (
                                                                                <div className="flex flex-wrap gap-1">
                                                                                    {relationLinks(file.relations)}
                                                                                </div>
                                                                            ) : (
                                                                                <span className="text-muted-foreground text-xs">—</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="py-2">
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                className="text-destructive hover:text-destructive"
                                                                                onClick={() => handleDelete(file.uid)}
                                                                                disabled={deletingId === file.uid}
                                                                                title="Delete file"
                                                                            >
                                                                                <Trash2 className="h-4 w-4" />
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    <TablePager
                                                        page={usageTrackedPage.page}
                                                        pageSize={usageTrackedPage.pageSize}
                                                        total={usageTrackedPage.total}
                                                        onPageChange={setUsagePage}
                                                        onPageSizeChange={(size) => {
                                                            setUsagePageSize(size);
                                                            setUsagePage(1);
                                                        }}
                                                    />
                                                </>
                                            )
                                        ) : visibleUntracked.length === 0 ? (
                                            <p className="text-sm text-muted-foreground py-4">No untracked files on disk.</p>
                                        ) : (
                                            <>
                                                <div className="overflow-x-auto">
                                                    <p className="text-xs text-muted-foreground mb-2">
                                                        These files are on disk but have no <code>files</code> record. They may
                                                        still be referenced by item fields, or be leftovers from uploads that
                                                        never received a relation.
                                                    </p>
                                                    <table className="w-full text-sm">
                                                        <thead>
                                                            <tr className="border-b text-left text-xs text-muted-foreground">
                                                                <th className="w-14 py-2 pr-2" />
                                                                <th className="py-2 pr-2">Path on disk</th>
                                                                <th className="py-2 pr-2">Status</th>
                                                                <th className="py-2 pr-2 text-right">Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {usageUntrackedPage.rows.map((file) => (
                                                                <tr key={file.name} className="border-b align-top last:border-0">
                                                                    <td className="py-2 pr-2">
                                                                        <UsageThumb name={file.name} />
                                                                    </td>
                                                                    <td className="py-2 pr-2">
                                                                        <a
                                                                            href={resolveLabwickUploadUrl(file.name)}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="font-mono text-xs text-blue-600 hover:underline break-all"
                                                                        >
                                                                            {file.name}
                                                                        </a>
                                                                    </td>
                                                                    <td className="py-2 pr-2">
                                                                        <Badge variant="secondary">Untracked</Badge>
                                                                    </td>
                                                                    <td className="py-2 pr-2 text-right">
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            onClick={() => handleTrack(file.name)}
                                                                            disabled={trackingName !== null}
                                                                        >
                                                                            {trackingName === file.name ? "Tracking…" : "Track"}
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <TablePager
                                                    page={usageUntrackedPage.page}
                                                    pageSize={usageUntrackedPage.pageSize}
                                                    total={usageUntrackedPage.total}
                                                    onPageChange={setUsagePage}
                                                    onPageSizeChange={(size) => {
                                                        setUsagePageSize(size);
                                                        setUsagePage(1);
                                                    }}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                </Card>

                <FileSharingEditorDialog
                    file={editingFile}
                    open={Boolean(editingFile)}
                    onOpenChange={(open) => {
                        if (!open) setEditingFile(null);
                    }}
                    onSaved={handleSharingSaved}
                />
            </div>
        </div>
    );
};

export default FilesPage;
